/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { WorkspacePersona, PersonaWithMetadata } from '@workspaces/models/personas';
import { WorkspaceUser } from '@workspaces/models/users';
import { personaFeature } from '@workspaces/store/persona.feature';
import { userManagementFeature } from '@workspaces/store/user-management.feature';
import { workspacePersonaFeature } from '@workspaces/store/workspace-persona.feature';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, startWith } from 'rxjs';
import { Persona } from 'src/app/models/personas.interface';
import { CognitoService } from 'src/app/services/cognito.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { AppState } from 'src/app/state/app.state';

export interface CollabraExportFormats {
  Format: string;
  Label: string;
}

export interface CollaboraMention {
  username: string;
  profile: string;
  label?: string;
}

export interface SocketWorkspaceDefinition {
  type: 'document_updated';
  workspace_id?: string;
}

type DefaultCollabraFn = (MessageId: string, Values: any) => void;

@Injectable()
export class CollabraService {
  isCollabraIframeInitialized = false;
  private exportFormats$ = new BehaviorSubject<CollabraExportFormats[]>([]); //Don't modify. Collabra format
  public exportFormats = this.exportFormats$.asObservable();
  private refreshCollabFlag$ = new BehaviorSubject(false);
  public refreshCollabFlag = this.refreshCollabFlag$.asObservable();
  private bindedCollabpostMessage: DefaultCollabraFn = () => {};

  constructor(
    protected readonly store: Store<AppState>,
    protected readonly config: ConfigurationService,

    public readonly cognitoService: CognitoService
  ) {}

  get exportFormatsInitialized() {
    return !!this.exportFormats$.value.length;
  }

  private slashPrefix(inputString: string): string {
    const prefixes = ['export', 'downloadas-'];
    if (!inputString) {
      return inputString;
    }

    const lowerInput = inputString.toLowerCase();
    let result = inputString;

    for (const prefix of prefixes) {
      if (lowerInput.startsWith(prefix)) {
        result = inputString.substring(prefix.length);
        break;
      }
    }
    if (result.length > 0) {
      return result.charAt(0).toUpperCase() + result.slice(1);
    }
    return result;
  }

  public refreshCollabra() {
    this.refreshCollabFlag$.next(!this.refreshCollabFlag$.value);
    setTimeout(() => {
      this.refreshCollabFlag$.next(!this.refreshCollabFlag$.value);
    }, 0);
  }

  public setExportFormats(exportFormats: CollabraExportFormats[]) {
    exportFormats.forEach(exportFormat => {
      exportFormat.Label = this.slashPrefix(exportFormat.Label);
    });
    this.exportFormats$.next(exportFormats);
  }

  public setCollabraPostMessageFunction(fn: (MessageId: string, Values: any) => void) {
    this.bindedCollabpostMessage = fn;
  }

  public sendCollaboraPostMessage(MessageId: string, Values: any) {
    this.bindedCollabpostMessage(MessageId, Values);
  }

  public exportDocument(format: string) {
    if (!this.exportFormatsInitialized) {
      throw Error('No export format available');
    }
    this.sendCollaboraPostMessage('Action_Export', { Format: format, Notify: true });
  }

  public getPossibleMentionList(workspaceId: string) {
    const users$ = this.store.select(userManagementFeature.selectData);
    const persona$ = this.store.select(personaFeature.selectData);
    const workspacePersona$ = this.store.select(workspacePersonaFeature.workspacePersonaData(workspaceId));
    const personasWithMetadata$ = combineLatest([persona$, workspacePersona$]).pipe(
      map(([persona, workspacePersona]) => this.combinePersonas(workspacePersona, persona))
    );

    return combineLatest([users$, personasWithMetadata$]).pipe(
      startWith([[], []]),
      distinctUntilChanged(),
      map(([users, personas]) => {
        return this.createMentionList(users, personas);
      })
    );
  }
  private combinePersonas(workspacePersonas: WorkspacePersona[], personas: Persona[]): PersonaWithMetadata[] {
    if (!workspacePersonas || !personas) {
      return [];
    }

    return workspacePersonas.map(workspacePersona => {
      const personaWithMeta: PersonaWithMetadata = {
        ...workspacePersona,
        ...(personas.find(persona => persona.id === workspacePersona.persona_id) ?? {
          name: 'Unknown name',
          current_prompt: 'Unknown prompt',
          exampleDocuments: []
        })
      };
      return personaWithMeta;
    });
  }

  private createMentionList(users: WorkspaceUser[], personas: PersonaWithMetadata[]): CollaboraMention[] {
    const userAssignes: CollaboraMention[] = users.map(user => ({
      username: user.id,
      label: user.email,
      profile: location.origin
    }));
    const personaAssignes: CollaboraMention[] = personas.map(persona => ({
      username: persona.id ?? '',
      label: persona.name ? persona.name + ' - Persona' : 'Unknown persona',
      profile: location.origin
    }));
    return [
      { label: 'Agent', profile: location.origin, username: 'agent' },
      { label: 'Task', profile: location.origin, username: 'task' },
      ...userAssignes,
      ...personaAssignes
    ];
  }

  public getCollabraConnectionUrl(documentId: string) {
    const driftApi = this.config.getConfiguration().driftApi;
    const accessToken = this.cognitoService.authenticationSubject.getValue()?.getIdToken().getJwtToken();
    const generatedWopiUrl = `${driftApi}/wopi/files/${documentId}`;
    const usedCollabora = location.origin + '/browser/c042625181/cool.html';
    return `${usedCollabora}?WOPISrc=${encodeURIComponent(generatedWopiUrl)}&access_token=${accessToken}`;
  }
}
